import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearAppLeaveGuard, setAppLeaveGuard, type AppLeaveGuard } from '@/lib/appLeaveGuard';

/**
 * Asks before in-app navigation while `when` is true.
 * Link clicks (menu, bottom bar, in-page links) are caught here.
 * Buttons that call `navigate` should use `confirmAppLeave` or `requestLeave`.
 */
export function useSaveBeforeLeave({
  when,
  onSave,
  onDiscard,
  beforeUnload = true,
}: {
  when: boolean;
  onSave: () => Promise<boolean>;
  onDiscard?: () => Promise<void> | void;
  /** Browser refresh/close. Journal already warns while autosave is still writing. */
  beforeUnload?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const pendingAction = useRef<(() => void) | null>(null);
  const allowLeave = useRef(false);
  const whenRef = useRef(when);
  const onSaveRef = useRef(onSave);
  const onDiscardRef = useRef(onDiscard);
  const requestLeaveRef = useRef<(action: () => void) => void>(() => undefined);
  whenRef.current = when;
  onSaveRef.current = onSave;
  onDiscardRef.current = onDiscard;

  function requestLeave(action: () => void) {
    if (!whenRef.current || allowLeave.current) {
      action();
      return;
    }
    pendingAction.current = action;
    setOpen(true);
  }
  requestLeaveRef.current = requestLeave;

  useEffect(() => {
    const guard: AppLeaveGuard = (proceed) => requestLeaveRef.current(proceed);
    setAppLeaveGuard(guard);
    return () => clearAppLeaveGuard(guard);
  }, []);

  useEffect(() => {
    if (!when || !beforeUnload) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [beforeUnload, when]);

  useEffect(() => {
    if (!when) return;
    function onClick(event: MouseEvent) {
      if (allowLeave.current || event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[role="alertdialog"]')) return;
      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }
      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === location.pathname && url.search === location.search) return;
      event.preventDefault();
      event.stopPropagation();
      pendingAction.current = () => {
        allowLeave.current = true;
        navigate(`${url.pathname}${url.search}${url.hash}`);
      };
      setOpen(true);
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [location.pathname, location.search, navigate, when]);

  function stay() {
    if (saving) return;
    pendingAction.current = null;
    setOpen(false);
  }

  async function discardAndLeave() {
    if (saving) return;
    setSaving(true);
    try {
      await onDiscardRef.current?.();
    } finally {
      const action = pendingAction.current;
      pendingAction.current = null;
      allowLeave.current = true;
      setOpen(false);
      setSaving(false);
      action?.();
    }
  }

  async function saveAndLeave() {
    if (saving) return;
    setSaving(true);
    try {
      const ok = await onSaveRef.current();
      if (!ok) return;
      const action = pendingAction.current;
      pendingAction.current = null;
      allowLeave.current = true;
      setOpen(false);
      action?.();
    } finally {
      setSaving(false);
    }
  }

  return { open, saving, requestLeave, stay, discardAndLeave, saveAndLeave };
}
